// src/contexts/ReportContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Report } from "@visitproof/types";
import { ReportService } from "../services/ReportService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ReportContextProps {
  loading: Loadings;
  error: Errors;
  status: "all" | "pending" | "approved" | "completed";
  setStatus: (status: "all" | "pending" | "approved" | "completed") => void;
  reports: Report[];
  selectedReport: Report | null;
  setSelectedReport: (reportId: string) => void;
  updateReport: (
    reportId: string,
    reportData: Partial<
      Omit<Report, "id" | "createdAt" | "updatedAt" | "updatedBy">
    >,
  ) => void;
  createReport: (
    reportData: Omit<Report, "id" | "createdAt" | "updatedAt" | "updatedBy">
  ) => Promise<Report>;
  deleteReportById: (reportId: string) => void;
}

const ReportContext = createContext<ReportContextProps | undefined>(undefined);

export const useReport = (): ReportContextProps => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error("useReport must be used within a ReportProvider");
  }
  return context;
};

export const ReportProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [status, setStatus] = useState<"all" | "pending" | "approved" | "completed">(
    localStorage.getItem("reportStatus") === null
      ? "all"
      : (localStorage.getItem("reportStatus") as
          | "all"
          | "pending"
          | "approved"
          | "completed") || "all",
  );
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  useEffect(() => {
    if (!user || !account?.id) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }
    const statusFilter = status && status !== "all" ? [where("status", "==", status)] : [];
    console.log("statusFilter", statusFilter);
    setError((prev) => ({ ...prev, list: null }));
    setLoading((prev) => ({ ...prev, list: true }));
    const docRef = query(
      collection(db, "reports"),
      where("accountId", "==", account.id),
      ...statusFilter,
    );
    const unsubscribe = onSnapshot(
      docRef,
      async (docSnapshot) => {
        const reportsData: Report[] = [];

        // Fetch all reports with their visit data
        for (const docSnap of docSnapshot.docs) {
          const data = docSnap.data() as Report;
          const reportWithId = { ...data, id: docSnap.id };

          // Fetch visit data if visitId exists
          if (reportWithId.visitId) {
            try {
              const visitDocRef = doc(db, "visits", reportWithId.visitId);
              const visitSnapshot = await getDoc(visitDocRef);
              if (visitSnapshot.exists()) {
                reportWithId.visit = { id: visitSnapshot.id, ...visitSnapshot.data() } as any;
              }
            } catch (error) {
              console.error(`Error fetching visit ${reportWithId.visitId}:`, error);
            }
          }

          reportsData.push(reportWithId);
        }

        setReports(reportsData);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching reports: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user, account, status]);

  useEffect(() => {
    if (!selectedReport) return;
    setError((prev) => ({ ...prev, details: null }));
    const docRef = doc(collection(db, "reports"), selectedReport.id);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSelectedReport((prev) => ({ ...prev, ...data }) as Report);
        }
        setLoading((prev) => ({ ...prev, details: false }));
      },
      (error) => {
        console.error("Error fetching document: ", error);
        setError((prev) => ({ ...prev, details: error }));
        setLoading((prev) => ({ ...prev, details: true }));
      },
    );
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport?.id]);

  const setSelectedReportById = async (reportId: string) => {
    const reportData = await ReportService.getReport(reportId);
    setSelectedReport(reportData);
  };

  const updateReport = async (
    reportId: string,
    updatedData: Partial<Omit<Report, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await ReportService.updateReport(reportId, {
      ...updatedData,
      updatedBy: user!.uid,
    });
    await setSelectedReportById(reportId);
  };

  const deleteReportById = async (reportId: string) => {
    await ReportService.deleteReport(reportId);
    setReports(reports.filter((report) => report.id !== reportId));
  };

  const createReport = async (
    reportData: Omit<Report, "id" | "createdAt" | "updatedAt" | "updatedBy">
  ): Promise<Report> => {
    const newReport = await ReportService.createReport({
      ...reportData,
      updatedBy: user!.uid,
    });
    setReports([newReport, ...reports]);
    return newReport;
  };

  return (
    <ReportContext.Provider
      value={{
        loading,
        error,
        status,
        setStatus,
        reports,
        selectedReport,
        updateReport,
        createReport,
        deleteReportById,
        setSelectedReport: setSelectedReportById,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};
